// ./src/components/features/Glovebox/GloveboxList/GloveboxList.tsx
// Design:
// - Responsive card grid on ALL screen sizes (1 col mobile, 2 cols md, 3 cols lg)
// - Top-mounted image banner (full-width) or icon banner for cards without images
// - Always-visible inline action buttons (top-right) — no hover dependency
// - Explicit "view full screen" button on image area — not image click
// - No card-level onClick — all interactions via explicit buttons
// - Framer-motion staggered entrance animations
// - Enhanced background-fade highlight for newly added/edited cards
// - SwipeableCard for mobile swipe-to-reveal actions
// - Full document names (no truncation on title)
//
// Context support:
// - When rendered inside a <GloveboxDocumentsProvider>, data props (documents,
//   loading, loadingMore, hasMore, onLoadMore, deletingId) are read from context
//   automatically. Props can still override context values if provided.
// - When rendered outside a provider, all data must be passed via props.

import React, { useMemo, useCallback, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  Skeleton,
  Tooltip,
  Image,
  Avatar,
  ActionIcon,
  Anchor,
  CopyButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { GloveboxDocument } from "@/utils/api";
import {
  useAppEssentialsContext,
  useDashboardContext,
  useOptionalGloveboxDocumentsContext,
} from "@/contexts";
import { getCategoryColor, getDocTypeIcon } from "@/utils/gloveboxHelpers";
import {
  AppIcon,
  EmptyState,
  LoadMoreButton,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  ImageViewer,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { formatDate } from "@/utils";
import { buildFullName } from "@/utils/nameHelpers";

// ============================================
// Types
// ============================================

export interface GloveboxListProps {
  /**
   * Array of glovebox documents to display.
   * Optional when inside a GloveboxDocumentsProvider — reads from context.
   */
  documents?: GloveboxDocument[];

  /**
   * Loading state for initial load.
   * Optional when inside a GloveboxDocumentsProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEdit?: (document: GloveboxDocument) => void;

  /** Callback when delete action is clicked */
  onDelete?: (document: GloveboxDocument) => void;

  /** ID of document currently being deleted. Optional — reads from context. */
  deletingId?: string | null;

  /** ID of document just edited or added (for highlighting) */
  highlightedId?: string | null;

  /** Callback for empty state action button */
  onAdd?: () => void;

  /** Whether to show vehicle/driver info */
  showVehicleDriver?: boolean;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const EXPIRING_SOON_DAYS = 30;

/** Height of the banner area at the top of each card (image or icon) */
const BANNER_HEIGHT = 160;

/**
 * Highlight style: persistent green left border + light background.
 * Stays active as long as highlightedId matches the card.
 * Parent clears highlightedId on next add/edit/refresh.
 */
const HIGHLIGHTED_STYLE: React.CSSProperties = {
  borderLeft: "3px solid var(--mantine-color-green-5)",
  backgroundColor: "var(--mantine-color-green-0)",
};

// ============================================
// Helper Functions
// ============================================

const getExpirationStatus = (document: GloveboxDocument) => {
  if (!document.expiresAt) return null;

  const isExpired = document.isExpired === true;
  const daysUntilExpiration = document.daysUntilExpiration;
  const isExpiringSoon =
    !isExpired &&
    daysUntilExpiration !== null &&
    daysUntilExpiration !== undefined &&
    daysUntilExpiration >= 0 &&
    daysUntilExpiration <= EXPIRING_SOON_DAYS;

  return { isExpired, isExpiringSoon, daysUntilExpiration };
};

const isImageFile = (mimeType?: string): boolean => {
  return mimeType?.startsWith("image/") || false;
};

const getUserInitials = (user: GloveboxDocument["user"]): string => {
  if (!user) return "??";
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "??";
};

/**
 * Normalize a website value into a full URL for href.
 * Prepends https:// if the value has no protocol.
 */
const normalizeWebsiteUrl = (website: string): string => {
  const trimmed = website.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Return a display-friendly version of a website URL
 * by stripping the protocol and trailing slash.
 */
const formatWebsiteDisplay = (website: string): string => {
  return website.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
};

// ============================================
// Loading Skeletons
// ============================================

const GloveboxCardSkeleton: React.FC = () => {
  return (
    <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
      {/* Image banner skeleton */}
      <Skeleton height={BANNER_HEIGHT} radius={0} />

      {/* Content skeleton */}
      <Stack gap="sm" p="md">
        <Group justify="space-between" wrap="nowrap">
          <Skeleton height={20} width="70%" />
          <Group gap={4}>
            <Skeleton height={28} width={28} radius="sm" />
            <Skeleton height={28} width={28} radius="sm" />
          </Group>
        </Group>
        <Skeleton height={20} width={60} radius="xl" />
        <Skeleton height={14} width="80%" />
        <Skeleton height={14} width="60%" />
        <Skeleton height={22} width={100} radius="xl" />
      </Stack>
    </Paper>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
        gap: "var(--mantine-spacing-md)",
      }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <GloveboxCardSkeleton key={index} />
      ))}
    </Box>
  );
};

// ============================================
// Card Image Banner Component
// ============================================

interface CardBannerProps {
  document: GloveboxDocument;
  onViewFullScreen?: () => void;
}

const CardBannerComponent: React.FC<CardBannerProps> = ({
  document,
  onViewFullScreen,
}) => {
  const { t } = useTranslation(["glovebox"]);
  const { getGloveboxDocTypeById } = useAppEssentialsContext();

  const docType =
    document.docType || getGloveboxDocTypeById(document.docTypeId);
  const hasImage = Boolean(
    document.uploadedFile?.fileUrl &&
    isImageFile(document.uploadedFile?.mimeType)
  );
  const categoryColor = getCategoryColor(docType?.category);
  const iconName = getDocTypeIcon(docType?.code, docType?.category);

  // Card WITH an uploaded image → full-width image banner
  if (hasImage) {
    return (
      <Box
        style={{
          position: "relative",
          height: BANNER_HEIGHT,
          overflow: "hidden",
          backgroundColor: `var(--mantine-color-${categoryColor}-0)`,
        }}
      >
        <Image
          src={
            document.uploadedFile?.thumbnailUrl ||
            document.uploadedFile?.fileUrl
          }
          alt={document.customTypeName || docType?.name || "Document"}
          fit="cover"
          h={BANNER_HEIGHT}
          w="100%"
          fallbackSrc={undefined}
        />

        {/* Explicit "View full screen" button — overlaid on image */}
        {onViewFullScreen && (
          <Tooltip
            label={t("glovebox:actions.viewImage")}
            withArrow
            position="left"
          >
            <ActionIcon
              variant="white"
              color="dark"
              size="md"
              radius="xl"
              onClick={onViewFullScreen}
              aria-label={t("glovebox:actions.viewImage")}
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            >
              <AppIcon iconName="Maximize" size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Card WITHOUT image → shorter colored banner with centered icon
  return (
    <Box
      style={{
        height: BANNER_HEIGHT,
        backgroundColor: `var(--mantine-color-${categoryColor}-0)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AppIcon
        iconName={iconName}
        size={48}
        color={`var(--mantine-color-${categoryColor}-4)`}
      />
    </Box>
  );
};

const CardBanner = React.memo(CardBannerComponent);

// ============================================
// Vehicle/Driver Display Component
// ============================================

interface VehicleDriverDisplayProps {
  document: GloveboxDocument;
  showIcon?: boolean;
  size?: "sm" | "md";
}

const VehicleDriverDisplay: React.FC<VehicleDriverDisplayProps> = ({
  document,
  showIcon = true,
  size = "sm",
}) => {
  const { getCarById } = useDashboardContext();

  const vehicle =
    document.car || (document.carId ? getCarById(document.carId) : null);
  const user = document.user;

  if (user) {
    const fullName = buildFullName(user);
    const initials = getUserInitials(user);

    return (
      <Group gap={6} wrap="nowrap">
        {showIcon && (
          <Avatar
            src={user.avatarUrl}
            size={size === "sm" ? 20 : 24}
            radius="xl"
            color="green"
          >
            {initials}
          </Avatar>
        )}
        <Text size={size} lineClamp={1}>
          {fullName}
        </Text>
      </Group>
    );
  }

  if (vehicle) {
    return (
      <Group gap={6} wrap="nowrap">
        {showIcon && (
          <AppIcon iconName="Car" size={size === "sm" ? 16 : 18} color="gray" />
        )}
        <Text size={size} lineClamp={1}>
          {vehicle.label || vehicle.model || "—"}
        </Text>
      </Group>
    );
  }

  return null;
};

// ============================================
// Clickable Contact Row Component
// ============================================

interface ContactRowProps {
  icon: string;
  label: string;
  value: string;
  href: string;
  copyValue: string;
  copyTooltip: string;
  copiedTooltip: string;
  target?: string;
}

const ContactRow: React.FC<ContactRowProps> = ({
  icon,
  label,
  value,
  href,
  copyValue,
  copyTooltip,
  copiedTooltip,
  target,
}) => (
  <Group gap={6} wrap="nowrap">
    <AppIcon iconName={icon} size={14} color="gray" style={{ flexShrink: 0 }} />
    <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
      {label}:
    </Text>
    <Anchor
      href={href}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      size="sm"
      underline="hover"
      style={{ minWidth: 0 }}
      lineClamp={1}
    >
      {value}
    </Anchor>
    <CopyButton value={copyValue} timeout={2000}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? copiedTooltip : copyTooltip} withArrow position="right">
          <ActionIcon
            variant="subtle"
            color={copied ? "teal" : "gray"}
            size="xs"
            onClick={copy}
            style={{ flexShrink: 0 }}
          >
            <AppIcon iconName={copied ? "Check" : "Copy"} size={12} />
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  </Group>
);

// ============================================
// GloveboxCard Component
// ============================================

interface GloveboxCardProps {
  document: GloveboxDocument;
  onEdit?: (document: GloveboxDocument) => void;
  onDelete?: (document: GloveboxDocument) => void;
  onViewFullScreen?: (document: GloveboxDocument) => void;
  highlighted: boolean;
  isDeleting: boolean;
  showVehicleDriver: boolean;
  /** Use swipe actions instead of inline buttons (mobile) */
  swipeable?: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const GloveboxCardComponent: React.FC<GloveboxCardProps> = ({
  document,
  onEdit,
  onDelete,
  onViewFullScreen,
  highlighted,
  isDeleting,
  showVehicleDriver,
  swipeable = false,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["glovebox", "common"]);
  const { getGloveboxDocTypeById } = useAppEssentialsContext();

  const docType =
    document.docType || getGloveboxDocTypeById(document.docTypeId);
  const hasImage = Boolean(
    document.uploadedFile?.fileUrl &&
    isImageFile(document.uploadedFile?.mimeType)
  );

  const documentName =
    document.customTypeName || docType?.name || t("glovebox:card.untitled");
  const categoryColor = getCategoryColor(docType?.category);

  const expirationStatus = useMemo(
    () => getExpirationStatus(document),
    [document]
  );

  const hasVehicleOrDriver = Boolean(
    document.car || document.carId || document.user
  );

  // Full-screen image viewer callback
  const handleViewFullScreen = useCallback(() => {
    onViewFullScreen?.(document);
  }, [onViewFullScreen, document]);

  // Swipe actions (mobile)
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    if (!swipeable) return [];

    const actions: SwipeAction[] = [];

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(document),
        disabled: isDeleting,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(document),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [swipeable, onEdit, onDelete, document, isDeleting, t]);

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => {
      onSwipeStateChange?.(isOpen);
    },
    [onSwipeStateChange]
  );

  // ── Card content ──
  const cardContent = (
    <Paper
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, background-color 200ms ease, border-color 200ms ease",
        position: "relative",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...(highlighted ? HIGHLIGHTED_STYLE : {}),
      }}
    >

      {/* ── Top: Image / Icon banner ── */}
      <CardBanner
        document={document}
        onViewFullScreen={hasImage ? handleViewFullScreen : undefined}
      />

      {/* ── Bottom: Card body ── */}
      <Stack gap="xs" p="md" style={{ flex: 1 }}>
        {/* Row 1: Title + action buttons */}
        <Group justify="space-between" gap="sm" wrap="nowrap" align="flex-start">
          <Text size="lg" fw={600}>
            {documentName}
          </Text>

          {/* Always-visible action buttons (desktop) */}
          {!swipeable && (onEdit || onDelete) && (
            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              {onEdit && (
                <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => onEdit(document)}
                    aria-label={t("common:buttons.edit")}
                  >
                    <AppIcon iconName="Edit" size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    loading={isDeleting}
                    disabled={isDeleting}
                    onClick={() => onDelete(document)}
                    aria-label={t("common:buttons.delete")}
                  >
                    <AppIcon iconName="Trash" size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}
        </Group>

        {/* Category badge */}
        <Box>
          <Badge variant="light" size="xs" color={categoryColor}>
            {docType?.category === "vehicle"
              ? t("glovebox:categories.vehicle")
              : docType?.category === "driver"
                ? t("glovebox:categories.driver")
                : docType?.category === "equipment"
                  ? t("glovebox:categories.equipment")
                  : t("glovebox:categories.other")}
          </Badge>
        </Box>

        {/* Document Number */}
        {document.documentNumber && (
          <Group gap={6} wrap="nowrap">
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
              {docType?.documentNumberLabel ||
                t("glovebox:card.documentNumber")}
              :
            </Text>
            <Text size="sm" fw={500}>
              {document.documentNumber}
            </Text>
          </Group>
        )}

        {/* Issuing Authority */}
        {document.issuingAuthority && (
          <Group gap={6} wrap="nowrap">
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
              {t("glovebox:card.issuedBy")}:
            </Text>
            <Text size="sm">{document.issuingAuthority}</Text>
          </Group>
        )}

        {/* Phone — tap to call, copy button */}
        {document.phone && (
          <ContactRow
            icon="Phone"
            label={t("glovebox:card.phone")}
            value={document.phone}
            href={`tel:${document.phone.replace(/\s+/g, "")}`}
            copyValue={document.phone}
            copyTooltip={t("common:buttons.copy")}
            copiedTooltip={t("common:buttons.copied")}
          />
        )}

        {/* Website — tap to open, copy button */}
        {document.website && (
          <ContactRow
            icon="World"
            label={t("glovebox:card.website")}
            value={formatWebsiteDisplay(document.website)}
            href={normalizeWebsiteUrl(document.website)}
            copyValue={document.website}
            copyTooltip={t("common:buttons.copy")}
            copiedTooltip={t("common:buttons.copied")}
            target="_blank"
          />
        )}

        {/* Vehicle or Driver */}
        {showVehicleDriver && hasVehicleOrDriver && (
          <VehicleDriverDisplay document={document} showIcon size="sm" />
        )}

        {/* Spacer to push expiration to the bottom */}
        <Box style={{ flex: 1 }} />

        {/* Expiration */}
        {document.expiresAt && (
          <Group gap={6}>
            <AppIcon
              iconName={
                expirationStatus?.isExpired ? "AlertCircle" : "Calendar"
              }
              size={14}
              color={
                expirationStatus?.isExpired
                  ? "red"
                  : expirationStatus?.isExpiringSoon
                    ? "orange"
                    : "gray"
              }
            />
            <Badge
              variant="light"
              size="md"
              color={
                expirationStatus?.isExpired
                  ? "red"
                  : expirationStatus?.isExpiringSoon
                    ? "orange"
                    : "gray"
              }
            >
              {expirationStatus?.isExpired
                ? t("glovebox:card.expired")
                : expirationStatus?.isExpiringSoon
                  ? t("glovebox:card.inDays", {
                    days: expirationStatus.daysUntilExpiration,
                  })
                  : formatDate(document.expiresAt)}
            </Badge>
          </Group>
        )}
      </Stack>
    </Paper>
  );

  // Wrap in SwipeableCard on mobile
  if (swipeable && leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isDeleting}
        borderRadius="var(--mantine-radius-md)"
      >
        {cardContent}
      </SwipeableCard>
    );
  }

  return cardContent;
};

const GloveboxCard = React.memo(GloveboxCardComponent);

// ============================================
// Main Component
// ============================================

const GloveboxListComponent: React.FC<GloveboxListProps> = ({
  documents: documentsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEdit,
  onDelete,
  deletingId: deletingIdProp,
  highlightedId,
  onAdd,
  showVehicleDriver = true,
  className,
}) => {
  const { t } = useTranslation(["glovebox", "common"]);

  // ── Context vs Props resolution ──
  // Props take priority so callers can override context values.
  const gloveboxContext = useOptionalGloveboxDocumentsContext();

  const documents = documentsProp ?? gloveboxContext?.documents ?? [];
  const loading = loadingProp ?? gloveboxContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? gloveboxContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? gloveboxContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? gloveboxContext?.loadMore;
  const deletingId = deletingIdProp ?? gloveboxContext?.removingId ?? null;

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageDoc, setSelectedImageDoc] =
    useState<GloveboxDocument | null>(null);

  // Swipeable list management (mobile)
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } =
    useSwipeableList();

  // Entrance animation — fires once when data first arrives
  const { hasAnimated } = useEntranceAnimation({
    itemCount: documents.length,
    staggerMs: 60,
  });

  // Handle swipe state change
  const handleSwipeStateChange = useCallback(
    (documentId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(documentId);
      }
    },
    [openCard]
  );

  // Wrap onEdit to close swiped card first
  const handleEdit = useCallback(
    (document: GloveboxDocument) => {
      closeAllCards();
      onEdit?.(document);
    },
    [closeAllCards, onEdit]
  );

  // Wrap onDelete to close swiped card first
  const handleDelete = useCallback(
    (document: GloveboxDocument) => {
      closeAllCards();
      onDelete?.(document);
    },
    [closeAllCards, onDelete]
  );

  // Open full-screen image viewer
  const handleViewFullScreen = useCallback((document: GloveboxDocument) => {
    setSelectedImageDoc(document);
    setImageViewerOpen(true);
  }, []);

  // Close image viewer
  const handleCloseImageViewer = useCallback(() => {
    setImageViewerOpen(false);
    setSelectedImageDoc(null);
  }, []);

  // Render a single card inside a motion.div
  const renderCard = useCallback(
    (doc: GloveboxDocument, isMobile: boolean) => (
      <motion.div
        key={doc.id}
        variants={hasAnimated ? undefined : entranceCardVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        exit="exit"
        layout
      >
        <GloveboxCard
          document={doc}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewFullScreen={handleViewFullScreen}
          highlighted={doc.id === highlightedId}
          isDeleting={deletingId === doc.id}
          showVehicleDriver={showVehicleDriver}
          swipeable={isMobile}
          forceClose={isMobile ? shouldForceClose(doc.id || "") : undefined}
          onForceCloseComplete={
            isMobile
              ? () => handleForceCloseComplete(doc.id || "")
              : undefined
          }
          onSwipeStateChange={
            isMobile
              ? (isOpen) => handleSwipeStateChange(doc.id || "", isOpen)
              : undefined
          }
        />
      </motion.div>
    ),
    [
      hasAnimated,
      handleEdit,
      handleDelete,
      handleViewFullScreen,
      highlightedId,
      deletingId,
      showVehicleDriver,
      shouldForceClose,
      handleForceCloseComplete,
      handleSwipeStateChange,
    ]
  );

  // Loading state
  if (loading && documents.length === 0) {
    return <LoadingSkeleton />;
  }

  // Empty state
  if (!loading && documents.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("glovebox:emptyState.title")}
          description={t("glovebox:emptyState.description")}
          iconName="FileText"
          actionLabel={t("glovebox:emptyState.actionLabel")}
          actionIcon="Plus"
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Box className={className}>
      {/* ── Mobile: Single-column cards with swipe ── */}
      <Box hiddenFrom="md">
        <motion.div
          variants={hasAnimated ? undefined : entranceListVariants}
          initial={hasAnimated ? false : "hidden"}
          animate={hasAnimated ? undefined : "visible"}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--mantine-spacing-md)",
          }}
        >
          <AnimatePresence mode="sync">
            {documents.map((doc) => renderCard(doc, true))}
          </AnimatePresence>
        </motion.div>
        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </Box>

      {/* ── Desktop: Responsive grid with inline actions ── */}
      <Box visibleFrom="md">
        <motion.div
          variants={hasAnimated ? undefined : entranceListVariants}
          initial={hasAnimated ? false : "hidden"}
          animate={hasAnimated ? undefined : "visible"}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gridAutoRows: "1fr",
            gap: "var(--mantine-spacing-md)",
          }}
        >
          <AnimatePresence mode="sync">
            {documents.map((doc) => renderCard(doc, false))}
          </AnimatePresence>
        </motion.div>
        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </Box>

      {/* Image Viewer Modal */}
      <ImageViewer
        opened={imageViewerOpen}
        onClose={handleCloseImageViewer}
        src={selectedImageDoc?.uploadedFile?.fileUrl || ""}
        alt={
          selectedImageDoc?.customTypeName ||
          selectedImageDoc?.docType?.name ||
          "Document"
        }
        fileName={selectedImageDoc?.uploadedFile?.name}
      />
    </Box>
  );
};

export const GloveboxList = React.memo(GloveboxListComponent);